import { useState } from 'react';
import { adminApi, type ApplicationTier, type ApplicationTierInput } from '../lib/api.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { InfoBubble } from '../components/Screen.js';
import { useCan, whyDisabled } from '../lib/capabilities.js';
import {
  AsyncBoundary,
  Button,
  EmptyState,
  Field,
  HoldButton,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
  inputStyle,
} from '../components/ui.js';

/**
 * The paid tiers an application sells — RidgeWill/auth#567.
 *
 * Each tier has a NAME, which is what the product checks ("Premium gets seven
 * vehicles") and what a subscription stores, and the RevenueCat ENTITLEMENT that
 * unlocks it. The order is the rank, lowest first.
 *
 * A TABLE WITH ITS OWN ACTIONS, like the Security tab's grants: add, edit, move
 * and remove, each saving the whole list. There is no Save button to forget.
 *
 * The server refuses to remove or rename a tier somebody holds, and says so;
 * that message is shown as it comes back rather than second-guessed here,
 * because only the server knows who holds what.
 */
export function ApplicationTiers({ applicationId, appSlug }: { applicationId: string; appSlug: string }) {
  const { state, reload } = useAsync(() => adminApi.applicationTiers(applicationId), [applicationId]);
  const [editing, setEditing] = useState<{ index: number | null } | null>(null);
  const action = useAction();
  const mayWrite = useCan('application:write');

  /** Every write is the whole list, so each action builds the next list and sends it. */
  const save = (next: ApplicationTierInput[], done: string) =>
    action
      .run(async () => {
        await adminApi.setApplicationTiers(applicationId, next);
        return done;
      })
      .then(() => reload());

  const asInput = (tiers: ApplicationTier[]): ApplicationTierInput[] =>
    tiers.map(({ id, name, entitlementKey }) => ({ id, name, entitlementKey }));

  return (
    <AsyncBoundary state={state}>
      {({ tiers }) => (
        <Panel
          title="Tiers"
          info={
            <InfoBubble label="What tiers are">
              The paid tiers this application sells, lowest first. The name is what the application checks
              and what a subscription stores, so a tier somebody holds cannot be renamed or removed. The
              RevenueCat entitlement is the one that unlocks it; it must start with &ldquo;{appSlug}_&rdquo;.
              Each environment keeps its own tiers, so set them the same way in dev, staging and production.
            </InfoBubble>
          }
          action={
            <Button
              disabled={action.running || !mayWrite}
              title={mayWrite ? undefined : whyDisabled('editing tiers')}
              onClick={() => setEditing({ index: null })}
            >
              + Add tier
            </Button>
          }
        >
          {action.outcome ? (
            <PanelBody>
              <OutcomeNote outcome={action.outcome} />
            </PanelBody>
          ) : null}

          {tiers.length === 0 ? (
            <EmptyState
              title="This application sells no paid tiers"
              suggestion="Add one to let people buy it, and to let staff grant it."
            />
          ) : (
            <Table columns={['Rank', 'Name', 'RevenueCat entitlement', '']} actionsLast>
              {tiers.map((t, i) => (
                <tr key={t.id}>
                  <Td>{i + 1}</Td>
                  <Td>
                    <strong>{t.name}</strong>
                  </Td>
                  <Td>
                    <code>{t.entitlementKey}</code>
                  </Td>
                  <Td action>
                    <span style={{ display: 'inline-flex', gap: '.35rem' }}>
                      <Button
                        disabled={action.running || !mayWrite || i === 0}
                        title="Move up the list, to a lower tier"
                        onClick={() => {
                          const next = asInput(tiers);
                          [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
                          void save(next, 'Order saved.');
                        }}
                      >
                        ↑
                      </Button>
                      <Button
                        disabled={action.running || !mayWrite || i === tiers.length - 1}
                        title="Move down the list, to a higher tier"
                        onClick={() => {
                          const next = asInput(tiers);
                          [next[i], next[i + 1]] = [next[i + 1]!, next[i]!];
                          void save(next, 'Order saved.');
                        }}
                      >
                        ↓
                      </Button>
                      <Button disabled={action.running || !mayWrite} onClick={() => setEditing({ index: i })}>
                        Edit
                      </Button>
                      <HoldButton
                        disabled={action.running || !mayWrite}
                        title="Hold for a second and a half to remove this tier. Refused if anybody holds it."
                        onHold={() =>
                          void save(
                            asInput(tiers).filter((_, j) => j !== i),
                            `Removed ${t.name}.`,
                          )
                        }
                      >
                        Remove
                      </HoldButton>
                    </span>
                  </Td>
                </tr>
              ))}
            </Table>
          )}

          {editing ? (
            <TierModal
              appSlug={appSlug}
              tier={editing.index === null ? null : tiers[editing.index]!}
              onClose={() => setEditing(null)}
              onSave={(t) => {
                const next = asInput(tiers);
                if (editing.index === null) next.push(t);
                else next[editing.index] = { ...t, id: tiers[editing.index]!.id };
                return adminApi.setApplicationTiers(applicationId, next).then(() => {
                  setEditing(null);
                  reload();
                });
              }}
            />
          ) : null}
        </Panel>
      )}
    </AsyncBoundary>
  );
}

function TierModal({
  appSlug,
  tier,
  onClose,
  onSave,
}: {
  appSlug: string;
  tier: ApplicationTier | null;
  onClose: () => void;
  onSave: (t: ApplicationTierInput) => Promise<void>;
}) {
  const [name, setName] = useState(tier?.name ?? '');
  const [entitlementKey, setEntitlementKey] = useState(tier?.entitlementKey ?? `${appSlug}_`);
  const action = useAction();

  return (
    <Modal
      title={tier ? `Edit ${tier.name}` : 'Add a tier'}
      subtitle={tier ? undefined : 'New tiers are added as the highest. Move them with the arrows afterwards.'}
      onClose={onClose}
      width={520}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={action.running || !name.trim() || entitlementKey.trim().length <= appSlug.length + 1}
            onClick={() =>
              void action.run(async () => {
                await onSave({ name: name.trim(), entitlementKey: entitlementKey.trim() });
                return 'Saved.';
              })
            }
          >
            {action.running ? 'Saving…' : 'Save'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <div style={{ display: 'grid', gap: '.8rem' }}>
        <Field
          label="Name"
          hint="Exactly as the application checks it, for example PREMIUM. Cannot be changed while anybody holds this tier."
        >
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus style={inputStyle} />
        </Field>
        <Field
          label="RevenueCat entitlement"
          hint={`The entitlement's lookup key in RevenueCat. Starts with ${appSlug}_, lowercase.`}
        >
          <input
            value={entitlementKey}
            onChange={(e) => setEntitlementKey(e.target.value.toLowerCase())}
            spellCheck={false}
            style={{ ...inputStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
          />
        </Field>
      </div>
    </Modal>
  );
}
